(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-apollo-configurator]";
  const OPEN_CART_FLAG = "apollo_open_cart_drawer_after_add";
  const STATE_PREFIX = "apollo_configurator_state_";
  const POLL_MS = 350;

  const DRAWER_TRIGGERS = [
    '[data-action="open-drawer"][data-drawer-id="sidebar-cart"]',
    '[aria-controls="sidebar-cart"]',
    "[data-cart-drawer-trigger]"
  ];

  const int = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const positiveInt = (value, fallback = 1) => {
    const parsed = int(value, fallback);
    return parsed > 0 ? parsed : fallback;
  };

  const parseJson = (element, fallback = {}) => {
    try {
      return element ? JSON.parse(element.textContent) : fallback;
    } catch (error) {
      console.error("Apollo Configurator: invalid JSON.", error);
      return fallback;
    }
  };

  const locale = () => document.documentElement.lang || "de-DE";

  const money = (cents, currency = "EUR") =>
    new Intl.NumberFormat(locale(), {
      style: "currency",
      currency
    }).format(Number(cents || 0) / 100);

  const decimal = (value) =>
    new Intl.NumberFormat(locale(), {
      maximumFractionDigits: 1
    }).format(Number(value || 0));

  const makeConfigurationId = () => {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    const random = Math.random()
      .toString(36)
      .slice(2, 6)
      .toUpperCase();

    return `APOLLO-${now.getFullYear()}${pad(
      now.getMonth() + 1
    )}${pad(now.getDate())}-${pad(
      now.getHours()
    )}${pad(now.getMinutes())}${pad(
      now.getSeconds()
    )}-${random}`;
  };

  const stateKey = (productId) =>
    `${STATE_PREFIX}${productId}`;

  const readSavedState = (productId) => {
    try {
      const saved = sessionStorage.getItem(
        stateKey(productId)
      );

      return saved ? JSON.parse(saved) : null;
    } catch (_error) {
      return null;
    }
  };

  const saveState = (productId, state) => {
    try {
      sessionStorage.setItem(
        stateKey(productId),
        JSON.stringify(state)
      );
    } catch (_error) {
      // State persistence is optional.
    }
  };

  const selected = (root, role) =>
    root.querySelector(
      `input[data-role="${role}"]:checked`
    );

  const selectedValues = (root, role) =>
    Array.from(
      root.querySelectorAll(
        `input[data-role="${role}"]:checked`
      )
    ).map((input) => input.value);

  const restoreSelections = (root, saved) => {
    if (!saved) return;

    const restoreRadio = (role, value) => {
      const match = Array.from(
        root.querySelectorAll(
          `input[data-role="${role}"]`
        )
      ).find(
        (input) =>
          input.value === value &&
          !input.disabled
      );

      if (match) {
        match.checked = true;
      }
    };

    const restoreChecks = (
      role,
      values = []
    ) => {
      root
        .querySelectorAll(
          `input[data-role="${role}"]`
        )
        .forEach((input) => {
          input.checked =
            values.includes(input.value) &&
            !input.disabled;
        });
    };

    restoreRadio(
      "solar-panel",
      saved.solarValue || ""
    );

    restoreRadio(
      "gridbox",
      saved.gridboxValue || ""
    );

    restoreRadio(
      "ev-charger",
      saved.chargerValue || ""
    );

    restoreChecks(
      "dc-cable",
      saved.cableValues
    );

    restoreChecks(
      "mounting",
      saved.mountingValues
    );
  };

  const openCartAfterReload = (cartUrl) => {
    let shouldOpen = false;

    try {
      shouldOpen =
        sessionStorage.getItem(
          OPEN_CART_FLAG
        ) === "1";

      if (shouldOpen) {
        sessionStorage.removeItem(
          OPEN_CART_FLAG
        );
      }
    } catch (_error) {
      return;
    }

    if (!shouldOpen) return;

    window.setTimeout(() => {
      const trigger = DRAWER_TRIGGERS
        .map((selector) =>
          document.querySelector(selector)
        )
        .find(Boolean);

      if (trigger) {
        trigger.dispatchEvent(
          new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window
          })
        );
      } else {
        window.location.assign(
          cartUrl || "/cart"
        );
      }
    }, 700);
  };

  const initialize = (root) => {
    if (
      !root ||
      root.dataset.apolloInitialized ===
        "true"
    ) {
      return;
    }

    const data = parseJson(
      root.querySelector(
        "[data-apollo-configuration-data]"
      )
    );

    if (
      !Array.isArray(
        data.storageVariants
      )
    ) {
      console.error(
        "Apollo Configurator: storage variant data is missing."
      );
      return;
    }

    root.dataset.apolloInitialized =
      "true";

    const variants = new Map(
      data.storageVariants.map(
        (variant) => [
          Number(variant.id),
          variant
        ]
      )
    );

    const variantIds = new Set(
      variants.keys()
    );

    const productId = String(
      data.productId ||
        root.dataset.productId ||
        ""
    );

    const currency =
      data.currency || "EUR";

    const minimum = positiveInt(
      data.storageMinimum,
      1
    );

    const maximum = Math.max(
      minimum,
      positiveInt(
        data.storageMaximum,
        6
      )
    );

    const panelsPerSystem =
      positiveInt(
        data.solarPanelsPerSystem,
        4
      );

    const pvWattsPerSystem =
      positiveInt(
        data.pvWattsPerSystem,
        2000
      );

    const cablePairsPerSystem =
      positiveInt(
        data.dcCablePairsPerSystem,
        2
      );

    const mountingPerPanel =
      positiveInt(
        data.mountingUnitsPerPanel,
        1
      );

    const section =
      root.closest(".shopify-section") ||
      document;

    const accessories =
      root.querySelector(
        "[data-apollo-solar-accessories]"
      );

    const errorBox =
      root.querySelector(
        "[data-apollo-error]"
      );

    const submitButton =
      root.querySelector(
        "[data-apollo-submit]"
      );

    const submitLabel =
      submitButton?.querySelector("span");

    const buttonTotal =
      root.querySelector(
        "[data-apollo-button-total]"
      );

    const summary = {
      storage: root.querySelector(
        "[data-apollo-summary-storage]"
      ),

      storageQuantity:
        root.querySelector(
          "[data-apollo-summary-storage-quantity]"
        ),

      solar: root.querySelector(
        "[data-apollo-summary-solar]"
      ),

      pv: root.querySelector(
        "[data-apollo-summary-pv-capacity]"
      ),

      cable: root.querySelector(
        "[data-apollo-summary-cable]"
      ),

      mounting: root.querySelector(
        "[data-apollo-summary-mounting]"
      ),

      gridbox: root.querySelector(
        "[data-apollo-summary-gridbox]"
      ),

      charger: root.querySelector(
        "[data-apollo-summary-charger]"
      ),

      total: root.querySelector(
        "[data-apollo-summary-total]"
      )
    };

    let calculation = null;
    let submitting = false;
    let lastSignature = "";

    restoreSelections(
      root,
      readSavedState(productId)
    );

    const setError = (
      message = ""
    ) => {
      if (!errorBox) return;

      errorBox.textContent = message;
      errorBox.hidden = !message;
    };

    const productForms = () =>
      Array.from(
        section.querySelectorAll(
          'form[action*="/cart/add"]'
        )
      );

    const productForm = () =>
      productForms().find((form) => {
        const input =
          form.querySelector(
            '[name="id"]'
          );

        return (
          input &&
          variantIds.has(
            int(input.value)
          )
        );
      }) ||
      productForms()[0] ||
      null;

    const variantInput = () => {
      const form = productForm();

      const inputs = [
        ...(form
          ? form.querySelectorAll(
              '[name="id"]'
            )
          : []),

        ...section.querySelectorAll(
          '[name="id"]'
        )
      ];

      return (
        inputs.find((input) =>
          variantIds.has(
            int(input.value)
          )
        ) ||
        inputs[0] ||
        null
      );
    };

    const quantityInput = () => {
      const form = productForm();

      const inputs = Array.from(
        new Set([
          ...(form
            ? form.querySelectorAll(
                '[name="quantity"]'
              )
            : []),

          ...section.querySelectorAll(
            '[name="quantity"]'
          )
        ])
      ).filter(
        (input) => !input.disabled
      );

      return (
        inputs.find(
          (input) =>
            input.offsetParent !== null
        ) ||
        inputs[0] ||
        null
      );
    };

    const variantFromUrl = () => {
      try {
        return int(
          new URL(
            window.location.href
          ).searchParams.get("variant")
        );
      } catch (_error) {
        return 0;
      }
    };

    const storageVariant = () => {
      const candidates = [
        variantInput()?.value,
        variantFromUrl(),
        root.dataset
          .defaultStorageVariantId,
        data.defaultStorageVariantId
      ];

      for (const candidate of candidates) {
        const id = int(candidate);

        if (variants.has(id)) {
          return variants.get(id);
        }
      }

      return (
        variants.values().next().value ||
        null
      );
    };

    const storageQuantity = () =>
      positiveInt(
        quantityInput()?.value,
        1
      );

    const inputPrice = (input) =>
      int(input?.dataset.price, 0);

    const inputTitle = (
      input,
      fallback
    ) =>
      input?.dataset.title?.trim() ||
      fallback;

    const calculate = () => {
      const storage = storageVariant();
      const quantity =
        storageQuantity();

      const solar = selected(
        root,
        "solar-panel"
      );

      const hasSolar = Boolean(
        solar?.value
      );

      if (accessories) {
        accessories.hidden =
          !hasSolar;
      }

      if (!hasSolar) {
        root
          .querySelectorAll(
            'input[data-role="dc-cable"], input[data-role="mounting"]'
          )
          .forEach((input) => {
            input.checked = false;
          });
      }

      const cable = hasSolar
        ? selected(root, "dc-cable")
        : null;

      const mounting = hasSolar
        ? selected(root, "mounting")
        : null;

      const gridbox = selected(
        root,
        "gridbox"
      );

      const charger = selected(
        root,
        "ev-charger"
      );

      const solarQuantity =
        hasSolar
          ? quantity *
            panelsPerSystem
          : 0;

      const cableQuantity =
        cable
          ? quantity *
            cablePairsPerSystem
          : 0;

      const mountingQuantity =
        mounting
          ? solarQuantity *
            mountingPerPanel
          : 0;

      const pvWatts =
        hasSolar
          ? quantity *
            pvWattsPerSystem
          : 0;

      const total =
        int(storage?.price) *
          quantity +
        inputPrice(solar) *
          solarQuantity +
        inputPrice(cable) *
          cableQuantity +
        inputPrice(mounting) *
          mountingQuantity +
        inputPrice(gridbox) *
          (gridbox?.value ? 1 : 0) +
        inputPrice(charger) *
          (charger?.value ? 1 : 0);

      const errors = [];

      if (!storage) {
        errors.push(
          "Die Speichervariante konnte nicht gelesen werden."
        );
      } else if (
        storage.available === false
      ) {
        errors.push(
          "Die gewählte Speichervariante ist derzeit nicht verfügbar."
        );
      }

      if (
        quantity < minimum ||
        quantity > maximum
      ) {
        errors.push(
          `Bitte wählen Sie zwischen ${minimum} und ${maximum} Speichersystemen.`
        );
      }

      if (
        [
          solar,
          cable,
          mounting,
          gridbox,
          charger
        ]
          .filter(
            (input) => input?.value
          )
          .some(
            (input) => input.disabled
          )
      ) {
        errors.push(
          "Eine ausgewählte Komponente ist derzeit nicht verfügbar."
        );
      }

      return {
        valid: errors.length === 0,
        errors,
        storage,
        quantity,
        solar,
        cable,
        mounting,
        gridbox,
        charger,
        hasSolar,
        solarQuantity,
        cableQuantity,
        mountingQuantity,
        pvWatts,
        total
      };
    };

    const render = (state) => {
      if (summary.storage) {
        summary.storage.textContent =
          state.storage?.title ||
          "Nicht verfügbar";
      }

      if (
        summary.storageQuantity
      ) {
        summary.storageQuantity.textContent =
          String(state.quantity);
      }

      if (summary.solar) {
        summary.solar.textContent =
          state.hasSolar
            ? `${state.solarQuantity} × ${inputTitle(
                state.solar,
                "Solarmodul"
              )}`
            : "Ohne Solarmodule";
      }

      if (summary.pv) {
        summary.pv.textContent =
          `${decimal(
            state.pvWatts / 1000
          )} kWp`;
      }

      if (summary.cable) {
        summary.cable.textContent =
          state.cable
            ? `${state.cableQuantity} × ${inputTitle(
                state.cable,
                "DC-Verlängerungskabel-Paar"
              )}`
            : "Nicht ausgewählt";
      }

      if (summary.mounting) {
        summary.mounting.textContent =
          state.mounting
            ? `${state.mountingQuantity} × ${inputTitle(
                state.mounting,
                "Montagesystem"
              )}`
            : "Nicht ausgewählt";
      }

      if (summary.gridbox) {
        summary.gridbox.textContent =
          state.gridbox?.value
            ? inputTitle(
                state.gridbox,
                "Sunpura GridBox GB-22"
              )
            : "Nicht ausgewählt";
      }

      if (summary.charger) {
        summary.charger.textContent =
          state.charger?.value
            ? inputTitle(
                state.charger,
                "EV-Ladestation"
              )
            : "Nicht ausgewählt";
      }

      const total = money(
        state.total,
        currency
      );

      if (summary.total) {
        summary.total.textContent =
          total;
      }

      if (buttonTotal) {
        buttonTotal.textContent =
          total;
      }

      setError(
        state.errors.join(" ")
      );

      if (submitButton) {
        submitButton.disabled =
          submitting || !state.valid;
      }
    };

    const persist = (state) => {
      saveState(productId, {
        quantity: state.quantity,

        solarValue:
          selected(
            root,
            "solar-panel"
          )?.value || "",

        cableValues:
          selectedValues(
            root,
            "dc-cable"
          ),

        mountingValues:
          selectedValues(
            root,
            "mounting"
          ),

        gridboxValue:
          selected(
            root,
            "gridbox"
          )?.value || "",

        chargerValue:
          selected(
            root,
            "ev-charger"
          )?.value || ""
      });
    };

    const update = () => {
      calculation = calculate();
      render(calculation);
      persist(calculation);
    };

    const restoreQuantity = () => {
      const saved =
        readSavedState(productId);

      const input =
        quantityInput();

      const quantity = int(
        saved?.quantity
      );

      if (
        input &&
        quantity >= minimum &&
        quantity <= maximum &&
        int(input.value, 1) !==
          quantity
      ) {
        input.value =
          String(quantity);

        input.dispatchEvent(
          new Event("input", {
            bubbles: true
          })
        );

        input.dispatchEvent(
          new Event("change", {
            bubbles: true
          })
        );
      }
    };

    const setSubmitting = (value) => {
      submitting = value;

      if (!submitButton) return;

      submitButton.disabled =
        value ||
        !calculation?.valid;

      submitButton.setAttribute(
        "aria-busy",
        value ? "true" : "false"
      );

      if (submitLabel) {
        submitLabel.textContent =
          value
            ? "Konfiguration wird hinzugefügt …"
            : "Konfiguration in den Warenkorb";
      }
    };

    const sharedProperties = (
      state,
      configurationId
    ) => ({
      _apollo_config_id:
        configurationId,

      _apollo_configurator:
        String(
          data.productTitle ||
            "Sunpura System"
        ),

      _apollo_storage_variant:
        String(
          state.storage?.title || ""
        ),

      _apollo_storage_system_quantity:
        String(state.quantity),

      _apollo_panels_per_system:
        String(
          state.hasSolar
            ? panelsPerSystem
            : 0
        ),

      _apollo_total_panel_quantity:
        String(
          state.solarQuantity
        ),

      _apollo_pv_capacity_watts:
        String(state.pvWatts),

      _apollo_created_at:
        new Date().toISOString()
    });

    const cartItem = (
      id,
      quantity,
      role,
      properties
    ) => ({
      id: Number(id),
      quantity: Number(quantity),

      properties: {
        ...properties,

        _apollo_role: role,

        _apollo_component_quantity:
          String(quantity)
      }
    });

    const buildItems = (
      state,
      configurationId
    ) => {
      const properties =
        sharedProperties(
          state,
          configurationId
        );

      const items = [
        cartItem(
          state.storage.id,
          state.quantity,
          "storage",
          properties
        )
      ];

      if (
        state.solar?.value &&
        state.solarQuantity
      ) {
        items.push(
          cartItem(
            state.solar.value,
            state.solarQuantity,
            "solar_panel",
            properties
          )
        );
      }

      if (
        state.cable?.value &&
        state.cableQuantity
      ) {
        items.push(
          cartItem(
            state.cable.value,
            state.cableQuantity,
            "dc_cable",
            properties
          )
        );
      }

      if (
        state.mounting?.value &&
        state.mountingQuantity
      ) {
        items.push(
          cartItem(
            state.mounting.value,
            state.mountingQuantity,
            "mounting",
            properties
          )
        );
      }

      if (state.gridbox?.value) {
        items.push(
          cartItem(
            state.gridbox.value,
            1,
            "gridbox",
            properties
          )
        );
      }

      if (state.charger?.value) {
        items.push(
          cartItem(
            state.charger.value,
            1,
            "ev_charger",
            properties
          )
        );
      }

      return items.filter(
        (item) =>
          item.id > 0 &&
          Number.isInteger(
            item.quantity
          ) &&
          item.quantity > 0
      );
    };

    const cartAddUrl = () => {
      if (data.cartAddUrl) {
        return data.cartAddUrl;
      }

      return `${
        window.Shopify?.routes
          ?.root || "/"
      }cart/add.js`;
    };

    const responseJson = async (
      response
    ) => {
      const text =
        await response.text();

      try {
        return text
          ? JSON.parse(text)
          : {};
      } catch (_error) {
        return {
          description: text
        };
      }
    };

    const submit = async () => {
      update();

      if (
        !calculation?.valid ||
        submitting
      ) {
        return;
      }

      const configurationId =
        makeConfigurationId();

      const items = buildItems(
        calculation,
        configurationId
      );

      if (!items.length) {
        setError(
          "Es konnten keine gültigen Artikel ermittelt werden."
        );
        return;
      }

      setError("");
      setSubmitting(true);

      try {
        const response = await fetch(
          cartAddUrl(),
          {
            method: "POST",

            credentials:
              "same-origin",

            headers: {
              Accept:
                "application/json",

              "Content-Type":
                "application/json",

              "X-Requested-With":
                "XMLHttpRequest"
            },

            body: JSON.stringify({
              items
            })
          }
        );

        const payload =
          await responseJson(
            response
          );

        if (!response.ok) {
          throw new Error(
            payload.description ||
              payload.message ||
              "Die Konfiguration konnte nicht hinzugefügt werden."
          );
        }

        document.dispatchEvent(
          new CustomEvent(
            "apollo:configuration-added",
            {
              bubbles: true,

              detail: {
                configurationId,
                items,
                response: payload
              }
            }
          )
        );

        try {
          sessionStorage.setItem(
            OPEN_CART_FLAG,
            "1"
          );
        } catch (_error) {
          // Continue without session storage.
        }

        window.location.reload();
      } catch (error) {
        console.error(
          "Apollo Configurator: add to cart failed.",
          error
        );

        setError(
          error instanceof Error
            ? error.message
            : "Die Konfiguration konnte nicht hinzugefügt werden."
        );

        setSubmitting(false);
      }
    };

    root.addEventListener(
      "change",
      (event) => {
        if (
          event.target.matches(
            "input[data-role]"
          )
        ) {
          update();
        }
      }
    );

    document.addEventListener(
      "change",
      (event) => {
        if (
          event.target.matches(
            '[name="id"], [name="quantity"]'
          )
        ) {
          window.setTimeout(
            update,
            0
          );
        }
      }
    );

    document.addEventListener(
      "input",
      (event) => {
        if (
          event.target.matches(
            '[name="quantity"]'
          )
        ) {
          window.setTimeout(
            update,
            0
          );
        }
      }
    );

    submitButton?.addEventListener(
      "click",
      submit
    );

    restoreQuantity();
    update();

    openCartAfterReload(
      data.cartUrl || "/cart"
    );

    const timer =
      window.setInterval(() => {
        if (!root.isConnected) {
          window.clearInterval(
            timer
          );
          return;
        }

        const next = calculate();

        const signature =
          JSON.stringify({
            variant:
              next.storage?.id || 0,

            quantity:
              next.quantity,

            solar:
              next.solar?.value || "",

            cable:
              next.cable?.value || "",

            mounting:
              next.mounting?.value ||
              "",

            gridbox:
              next.gridbox?.value || "",

            charger:
              next.charger?.value || ""
          });

        if (
          signature !==
          lastSignature
        ) {
          lastSignature =
            signature;

          calculation = next;

          render(next);
          persist(next);
        }
      }, POLL_MS);
  };

  const initializeAll = () =>
    document
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(initialize);

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initializeAll,
      {
        once: true
      }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    "shopify:section:load",
    initializeAll
  );

  window.ApolloConfigurator = {
    initializeAll,
    formatMoney: money
  };
})();